const validInputRegex = /^\s*(\d+(-\d+)?)(,\s*\d+(-\d+)?)*\s*$/;
type Range = {start: number, end: number};

/**
 * Parses a string in the form of "3" or "1,2,3" or "1-3". In all three cases the result would be 3.
 */
export function parseLastVersion(versions: string): number {
    // Check if the input string matches any of the expected formats
    if (!versions.match(validInputRegex)) {
        throw new Error(`Invalid input format. Expected format: '3' or '1,2,3' or '1-3'. Received: ${versions}`);
    }

    const parts = versions.split(/[,-]/).map(part => parseInt(part.trim(), 10));
    return Math.max(...parts);
}

/**
 * Adds a number to the versions string.
 */
export function addVersion(versions: string, version: number): string {
    return mergeVersionStrings(versions, String(version));
}

/**
 * Merges two versions strings. Given two versions strings, this will return a string that represents the union of
 * both strings. It is assumed that both strings are ordered.
 */
export function mergeVersionStrings(a: string, b: string): string {
    const rangesA = parseVersionString(a);
    const rangesB = parseVersionString(b);
    const mergedRanges = optimizeRanges(rangesA.concat(rangesB));
    return formatVersionString(mergedRanges);
}

/**
 * Converts a versionString to an array of ranges
 */
function parseVersionString(versionString: string): Range[] {
    const ranges: Range[] = [];
    if (!versionString || !versionString.trim()) {
        return ranges;
    }
    const parts = versionString.split(',');
    for (const part of parts) {
        if (part.includes('-')) {
            const [start, end] = part.split('-').map(Number);
            ranges.push({start, end});
        } else {
            const num = Number(part);
            ranges.push({start: num, end: num});
        }
    }
    return optimizeRanges(ranges);
}

function formatVersionString(ranges: Range[]): string {
    return ranges
        .map((range: Range) => (range.start === range.end ? `${range.start}` : `${range.start}-${range.end}`))
        .join(',');
}

function optimizeRanges(ranges: Range[]): Range[] {
    return ranges
        .sort((a, b) => a.start - b.start)
        .reduce<Range[]>((optimized, current) => {
            const last = optimized[optimized.length - 1];
            if (!last || current.start > last.end + 1) {
                optimized.push(current);
            } else {
                last.end = Math.max(last.end, current.end);
            }
            return optimized;
        }, []);
}

/**
 * Detect gaps in a versions string. Given a version string, this will return an array of objects with the start and
 * end of each gap.  If there isn't any gap, it will return an empty array.
 */
export function getVersionGaps(versions: string): Range[] {
    if (!versions || !versions.trim()) return [];

    const parsedVersions: Range[] = parseVersionString(versions);

    return parsedVersions.reduce<Range[]>((gaps, current, index, arr) => {
        const prevEnd = index === 0 ? 0 : arr[index - 1].end;
        if (current.start > prevEnd + 1) {
            gaps.push({ start: prevEnd + 1, end: current.start - 1 });
        }
        return gaps;
    }, []);
}

/**
 * Returns the current time in microseconds. We use this function to give a more detailed timestamp to the operations.
 */
export function nowInMicroseconds(): number {
    return (performance.now() + performance.timeOrigin) * 10;
}
